import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import BrandMark from './BrandMark';
import UserAvatar from './UserAvatar';
import TierFrame from './TierFrame';
import ReputationBadge from './ReputationBadge';
import Icon from './Icon';
import { useAuth } from '../context/AuthContext';
import { notificationsApi } from '../api/notificationsApi';
import { messagingApi } from '../api/messagingApi';

// The app's chrome — glass-blur top bar, sticky left rail on desktop,
// floating tab bar on mobile widths. Visual language matches the
// mobile app's Arena: real icons (SVG via <Icon />), gradient
// accent on the active nav item, subtle hover lift.
const NAV = [
  { to: '/app',                 label: 'Arena',    icon: 'home',    activeIcon: 'home-filled' },
  { to: '/app/reels',           label: 'Reels',    icon: 'reels' },
  { to: '/app/battles',         label: 'Battles',  icon: 'battles' },
  { to: '/app/create',          label: 'Create',   icon: 'plus' },
  { to: '/app/notifications',   label: 'Inbox',    icon: 'bell' },
  { to: '/app/messages',        label: 'Messages', icon: 'chat' }
];

const RAIL_EXTRA = [
  { to: '/app/search',      label: 'Search',      icon: 'search' },
  { to: '/app/activity',    label: 'Activity',    icon: 'history' },
  { to: '/app/leaderboard', label: 'Leaderboard', icon: 'trophy' }
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [unreadDms, setUnreadDms] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchTerm.trim();
    navigate(q ? `/app/search?q=${encodeURIComponent(q)}` : '/app/search');
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const refresh = async () => {
      try {
        const [n, m] = await Promise.all([
          notificationsApi.summary().catch(() => null),
          messagingApi.unreadSummary().catch(() => null)
        ]);
        if (!alive) return;
        setUnreadNotifs(n?.unread || 0);
        setUnreadDms(m?.unread || 0);
      } catch {}
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  if (!user) return null;

  const NavBadge = ({ count }) => count > 0
    ? <span className="rail-badge">{count > 99 ? '99+' : count}</span>
    : null;

  return (
    <div className="app-shell">
      <header className="app-bar">
        <div className="container app-bar-row">
          <Link to="/app" className="brand-link" aria-label="Outglow home">
            <BrandMark size={28} />
            <span className="brand-word">Outglow</span>
          </Link>

          <form onSubmit={submitSearch} className="app-search" role="search">
            <Icon name="search" size={16} />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search creators, #hashtags, battles"
              aria-label="Search"
            />
          </form>

          <div className="app-bar-right">
            <Link to="/app/search" className="icon-btn app-search-mobile" aria-label="Search">
              <Icon name="search" size={18} />
            </Link>
            <Link to={`/app/profile/${user.id || user._id}`} className="user-chip" aria-label="Your profile">
              <TierFrame user={user} size={30} ringWidth={2}>
                <UserAvatar user={user} size={26} />
              </TierFrame>
              <span className="user-chip-name">{user.displayName || user.username}</span>
              <ReputationBadge value={user.reputation || 0} />
            </Link>
            <Link to="/app/settings" className="icon-btn" aria-label="Settings">
              <Icon name="settings" size={18} />
            </Link>
            <button
              className="icon-btn"
              aria-label="Sign out"
              onClick={async () => { await logout(); navigate('/'); }}
            >
              <Icon name="signout" size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="container app-grid">
        <aside className="left-rail">
          <nav className="rail-group" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/app'}
                className={({ isActive }) => `rail-link ${isActive ? 'rail-link-active' : ''}`}
              >
                {({ isActive }) => (
                  <>
                    <span className="rail-icon" aria-hidden="true">
                      <Icon name={isActive && n.activeIcon ? n.activeIcon : n.icon} size={20} />
                    </span>
                    <span>{n.label}</span>
                    {n.to === '/app/notifications' ? <NavBadge count={unreadNotifs} /> : null}
                    {n.to === '/app/messages' ? <NavBadge count={unreadDms} /> : null}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="rail-divider" />

          <nav className="rail-group" aria-label="Discover">
            {RAIL_EXTRA.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `rail-link rail-link-sm ${isActive ? 'rail-link-active' : ''}`}
              >
                <span className="rail-icon" aria-hidden="true">
                  <Icon name={n.icon} size={18} />
                </span>
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="main-col">
          <Outlet />
        </main>
      </div>

      <nav className="bottom-bar" aria-label="Bottom navigation">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/app'}
            className={({ isActive }) => `tab ${isActive ? 'tab-active' : ''}`}
          >
            {({ isActive }) => (
              <>
                <Icon name={isActive && n.activeIcon ? n.activeIcon : n.icon} size={22} />
                <span className="tab-label">{n.label}</span>
                {n.to === '/app/notifications' && unreadNotifs > 0 ? <span className="tab-badge">{unreadNotifs > 9 ? '9+' : unreadNotifs}</span> : null}
                {n.to === '/app/messages' && unreadDms > 0 ? <span className="tab-badge">{unreadDms > 9 ? '9+' : unreadDms}</span> : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <style>{`
        .app-shell {
          min-height: 100vh;
          padding-bottom: 92px;
        }
        .app-bar {
          position: sticky; top: 0; z-index: 50;
          backdrop-filter: saturate(180%) blur(14px);
          -webkit-backdrop-filter: saturate(180%) blur(14px);
          background: rgba(8, 6, 14, 0.72);
          border-bottom: 1px solid var(--line-soft);
          padding: 12px 0;
        }
        .app-bar-row {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .brand-link {
          display: inline-flex; align-items: center; gap: 10px;
          text-decoration: none;
          color: var(--text);
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 18px;
          letter-spacing: -0.3px;
        }
        .brand-word {
          background: var(--grad-glow);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        @media (max-width: 760px) {
          .brand-word { display: none; }
        }

        .app-search {
          flex: 1;
          max-width: 460px;
          display: flex; align-items: center; gap: 8px;
          padding: 8px 14px;
          background: var(--card);
          border: 1px solid var(--line-soft);
          border-radius: 999px;
          color: var(--muted);
          transition: border-color .15s ease, background .15s ease;
        }
        .app-search:focus-within {
          border-color: var(--primary);
          background: var(--card);
          box-shadow: 0 0 0 3px rgba(255, 62, 127, .12);
        }
        .app-search input {
          flex: 1; border: none; background: none; padding: 0;
          color: var(--text); font-size: 14px;
        }
        .app-search input:focus { outline: none; box-shadow: none; }
        .app-search-mobile { display: none; }
        @media (max-width: 760px) {
          .app-search { display: none; }
          .app-search-mobile { display: inline-flex; }
        }

        .app-bar-right {
          display: flex; align-items: center; gap: 8px;
          margin-left: auto;
        }
        .icon-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 38px; height: 38px;
          border-radius: 12px;
          background: var(--card);
          color: var(--text);
          border: 1px solid var(--line-soft);
          transition: border-color .12s ease, transform .12s ease, background .12s ease;
        }
        .icon-btn:hover {
          border-color: var(--primary);
          text-decoration: none;
          color: var(--text);
        }
        .icon-btn:active { transform: translateY(1px); }

        .user-chip {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 4px 12px 4px 4px;
          border-radius: 999px;
          background: var(--card);
          border: 1px solid var(--line-soft);
          color: var(--text);
          text-decoration: none;
          transition: border-color .12s ease;
        }
        .user-chip:hover { border-color: var(--primary); text-decoration: none; }
        .user-chip-name {
          font-weight: 800; font-size: 13px;
          max-width: 110px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        @media (max-width: 760px) {
          .user-chip-name { display: none; }
          .user-chip { padding: 4px; }
        }

        .app-grid {
          display: grid;
          grid-template-columns: 240px minmax(0, 1fr);
          gap: 32px;
          padding-top: 28px;
        }
        .left-rail {
          position: sticky;
          top: 88px;
          align-self: start;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .rail-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .rail-divider {
          height: 1px;
          background: var(--line-soft);
          margin: 4px 8px;
        }
        .rail-link {
          display: flex; align-items: center; gap: 12px;
          padding: 11px 14px;
          border-radius: 12px;
          color: var(--text-dim);
          font-weight: 800;
          font-size: 14px;
          position: relative;
          text-decoration: none;
          transition: background .12s ease, color .12s ease, transform .12s ease;
        }
        .rail-link:hover { background: var(--card); color: var(--text); text-decoration: none; }
        .rail-link-active {
          background: var(--card);
          color: var(--text);
          border: 1px solid var(--line-soft);
          box-shadow: inset 0 0 0 1px rgba(255, 62, 127, 0.18);
        }
        .rail-link-active::before {
          content: '';
          position: absolute;
          left: -1px;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 60%;
          border-radius: 2px;
          background: var(--grad-glow);
          box-shadow: 0 0 12px rgba(255, 62, 127, 0.55);
        }
        .rail-link-sm { font-size: 13px; color: var(--muted); padding: 9px 14px; }
        .rail-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 24px; height: 24px;
        }
        .rail-link-active .rail-icon { color: var(--primary); }
        .rail-badge {
          margin-left: auto;
          min-width: 20px; height: 20px;
          padding: 0 6px; border-radius: 999px;
          background: var(--primary); color: #1a0a14;
          font-size: 11px; font-weight: 900;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .main-col {
          width: min(660px, 100%);
        }

        .bottom-bar { display: none; }

        @media (max-width: 760px) {
          .app-grid { grid-template-columns: 1fr; padding-top: 16px; gap: 16px; }
          .left-rail { display: none; }
          .main-col { width: 100%; }
          .bottom-bar {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            position: fixed; bottom: 12px; left: 12px; right: 12px;
            background: rgba(14, 11, 22, 0.92);
            backdrop-filter: saturate(180%) blur(16px);
            -webkit-backdrop-filter: saturate(180%) blur(16px);
            border: 1px solid var(--line-soft);
            border-radius: 18px;
            padding: 8px 6px 10px;
            z-index: 60;
            box-shadow: 0 18px 32px rgba(0, 0, 0, 0.35);
          }
          .tab {
            display: flex; flex-direction: column; align-items: center; gap: 3px;
            padding: 6px 0;
            color: var(--muted);
            text-decoration: none;
            position: relative;
            border-radius: 12px;
          }
          .tab-active {
            color: var(--text);
            background:
              radial-gradient(circle at 50% 100%, rgba(255, 62, 127, 0.25), transparent 70%);
          }
          .tab-active::after {
            content: '';
            position: absolute; top: 2px; left: 50%;
            transform: translateX(-50%);
            width: 18px; height: 3px;
            border-radius: 2px;
            background: var(--grad-glow);
          }
          .tab-label {
            font-size: 10px; font-weight: 800;
            letter-spacing: .2px;
          }
          .tab-badge {
            position: absolute; top: 2px; right: calc(50% - 22px);
            min-width: 18px; height: 18px;
            padding: 0 5px; border-radius: 999px;
            background: var(--primary); color: #1a0a14;
            font-size: 10px; font-weight: 900;
            display: inline-flex; align-items: center; justify-content: center;
            border: 2px solid rgba(14, 11, 22, 0.92);
          }
        }
      `}</style>
    </div>
  );
}
